
import Vue from "vue";
import Scorecard from "@/features/Scores/Scorecard";
import { DataTableOptions, PagingParameter } from "@/shared/models/PagingParameter";
import { ScorecardListResponse } from "@/features/Scores/ScoreService";
import AppError from "@/shared/components/AppError.vue";
import { formatDatepickerDate, parseDateAsUTC } from "@/shared/utils/common_utils";

export default Vue.extend({
    name: "ScorecardList",
    components: {
        AppError,
    },
    props: {
        // TODO #3738210: Include date range props. Watch for when they change to fetch scorecards with date range filters.
        supplierID: {
            type: String,
            required: false,
            default: "",
        },
        scorecardListOptions: {
            default: {
                sortBy: ["id"],
                sortDesc: [false],
                itemsPerPage: 10,
                page: 1,
            } as DataTableOptions,
            required: true,
        },
        beforeDate: {
            default: new Date().toISOString(),
            required: true,
        },
        afterDate: {
            default: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(), // one year ago today
            required: true,
        },
    },
    data: () => ({
        headers: [
            {
                text: "ID",
                value: "id",
                sortable: true,
                align: "start",
            },
            {
                text: "Assessment Date",
                value: "assessmentDate",
                sortable: true,
                align: "start",
            },
            {
                text: "Created by",
                value: "createdBy.name",
                sortable: true,
                align: "start",
            },
            {
                text: "Percentage score",
                value: "percentageScore",
                sortable: false,
                align: "end",
            },
        ],
        scorecards: [] as Scorecard[],
        totalScorecards: 0,
        loading: true,
        errorMessage: "",
        showError: false,
    }),
    watch: {
        scorecardListOptions: {
            handler() {
                this.getScorecards();
            },
            deep: true,
        },
        supplierID() {
            this.getScorecards();
        },
        afterDate(newValue, oldValue) {
            // Only re-fetch if the days have changed; disregard any changes to the time
            if (new Date(newValue).toDateString() != new Date(oldValue).toDateString()) {
                this.getScorecards();
            }
        },
        beforeDate(newValue, oldValue) {
            // Only re-fetch if the days have changed; disregard any changes to the time
            if (new Date(newValue).toDateString() != new Date(oldValue).toDateString()) {
                this.getScorecards();
            }
        },
    },
    created: function () {
        this.getScorecards();
    },
    methods: {
        formatDatepickerDateForTable(assessmentDate: string): string {
            const date = parseDateAsUTC(assessmentDate);
            return formatDatepickerDate(date);
        },
        formatPercentageForTable(float: number): string {
            const result = (float * 100).toFixed(0);
            return (result + "%") as string;
        },
        async getScorecards(): Promise<void> {
            try {
                if (this.supplierID) {
                    this.loading = true;
                    const pagingParam = new PagingParameter(this.scorecardListOptions, this.afterDate, this.beforeDate);
                    var response = (await this.$scoreService.getScorecardsFromServer({
                        ...pagingParam,
                        supplierID: this.supplierID,
                    })) as ScorecardListResponse;
                    this.scorecards = response.scorecards as Scorecard[];
                    this.totalScorecards = response.totalScorecards;
                }
            } catch (e) {
                this.errorMessage = "Failed to fetch supplier scorecard data";
                this.showError = true;
            } finally {
                this.loading = false;
            }
        },
        handleChangeScorecardTableOptions(newOptions: DataTableOptions) {
            this.$emit("scorecard-sort-updated", newOptions);
        },
        onListItemClicked(scorecardID: string) {
            this.$emit("scorecard-list-item-clicked", scorecardID);
        },
    },
});
